/**
 * Shared TypeScript types and helpers for form-field components in `component/Form/*`.
 * Every form input extends `FieldApiProps` for label / helper / error / disabled / required / size / minChars,
 * and consumes the helpers below for consistent debounce timing, validation-message resolution,
 * and char-count-driven minimum widths.
 *
 * Hooks: `useDebouncedCommit<T>` owns the keystroke→parent debounce
 * so consumer bodies stay focused on their component-specific concerns
 * (formatting, prefix/suffix, char count, steppers) instead of duplicating timer plumbing.
 */
import { type MouseEvent, type RefObject, useCallback, useEffect, useRef } from 'react'

/** Relative width of an item within its layout row.
 * Relative sizes (small, medium, large, extra-large) adapt per breakpoint tier.
 * Absolute sizes (half, full, remaining) hold across tiers.
 * Fields map the names to styles via the semantic --input-width-* scale.
 * Sections map the same names to their own --section-width-* scale with different steps.
 *
 * All together this allows for dynamic & responsive layouts with multiple levels of nesting.
 * Sizing is relative to its container rather than a rigid single-layer grid. */
export type LayoutSize =
  | 'small'
  | 'medium'
  | 'large'
  | 'extra-large'
  | 'half'
  | 'full'
  | 'remaining'

/** Props every form input exposes. Optionals accept `undefined` explicitly
 * so consumers can pass a variable that may or may not have a value without conditional spreads. */
export interface FieldApiProps {
  /** Visible label above the control. Truncates with tooltip when overflowing. */
  label?: string | undefined
  /** Helper text below the control. Replaced by errorMessage when present. */
  helperText?: string | undefined
  /** Error message below the control. Sets aria-invalid and colors the shell ring. */
  errorMessage?: string | undefined
  /** When set, the control is disabled and the message is exposed via the outer Tooltip + aria-disabled.
   * Presence implies disabled (mirrors Button). */
  disabledMessage?: string | undefined
  /** Renders an asterisk after the label and forwards `required` to the native input
   * so browsers fire HTML validity on submit. */
  required?: boolean | undefined
  /** Optional DOM id. When omitted, useId() generates one. */
  id?: string | undefined
  /** Relative width within the field's layout row. */
  size?: LayoutSize | undefined
  /** Floor on field width measured in characters. Resulting width is max(size, minChars). */
  minChars?: number | undefined
}

/** Debounce window for committing keystrokes to the parent. */
export const formFieldDebounceWindowMs = 250

/** Make the whole shell one click target:
 * a click on its dead space (padding / adornment gap) focuses the inner control.
 * Guarded on `target === currentTarget` so clicks on the control or an adornment keep native behavior.
 * preventDefault avoids a focus-steal flicker. */
export const forwardShellFocus = (
  event: MouseEvent<HTMLDivElement>,
  control: RefObject<HTMLInputElement | HTMLTextAreaElement | null>,
): void => {
  if (event.target !== event.currentTarget) {
    return
  }
  event.preventDefault()
  control.current?.focus()
}

/** Min-width floor ensuring `minChars` supplied fit in field: `(minChars+1)ch` (value + one-char caret cushion)
 * plus the field's horizontal chrome — shell padding/border and the Row gutter (`--action-gap`).
 * This sizes the editable area to match supplied intention, not the whole field box. */
export const getMinWidthStyle = (minChars: number): string =>
  `calc(${String(minChars + 1)}ch + 2 * var(--input-padding-inline) + 2 * var(--border-width) + var(--action-gap))`

/** Hold the latest `onChange` in a ref and commit values on a `formFieldDebounceWindowMs` timer.
 * Returns:
 *   - `scheduleCommit` — queue a value to commit after the window.
 *   - `cancelPendingCommit` — clear any pending timer.
 *     Call before any non-debounced commit path like blur or stepper.
 * The timer is cleared on unmount automatically. */
export const useDebouncedCommit = <T>(
  onChange: (value: T) => void,
): { scheduleCommit: (value: T) => void; cancelPendingCommit: () => void } => {
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelPendingCommit = useCallback((): void => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  const scheduleCommit = useCallback(
    (next: T): void => {
      cancelPendingCommit()
      timeoutRef.current = setTimeout(() => {
        onChangeRef.current(next)
        timeoutRef.current = null
      }, formFieldDebounceWindowMs)
    },
    [cancelPendingCommit],
  )

  // Cancel any pending commit on unmount.
  useEffect(() => cancelPendingCommit, [cancelPendingCommit])

  return { scheduleCommit, cancelPendingCommit }
}
